import React, { FC, MouseEvent, useState } from "react";

import { useTranslation } from "react-i18next";
import { Hidden, ListItemText, MenuItem } from "@mui/material";

import { RafterDownIcon } from "@/assets/icons";
import { LineSeparator } from "@/components/atoms";
import { brokerApi } from "@/shared/apis";
import { UrlManager } from "@/shared/apis/UrlManager";
import { UserState } from "@/shared/domain";
import { isABroker, useAuth } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import { StyledAccountMenu } from "../AccountInformation/styled";
import { AddBrokerButton } from "./components/AddBrokerButton/AddBrokerButton";
import CurrentBrokerInfo from "./components/CurrentBrokerInfo/CurrentBrokerInfo";
import MyBrokerSelectorMobile from "./components/MyBrokerSelectorMobile";
import { StyledModalMyBrokerSelectorMobile } from "./components/styled";
import { StyledBrokerButton, StyledUnselectedBrokerImage } from "./styled";

type UnselectedBrokerProps = {
    broker: MyBrokerApi.Broker;
    onBrokerChange: () => void;
};

const UnselectedBroker: FC<UnselectedBrokerProps> = ({ broker, onBrokerChange }) => {
    const handleKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === "Enter") {
            onBrokerChange();
        }
    };
    return (
        <MenuItem
            style={{ padding: "10px 20px" }}
            classes={{ root: "menuitem-root" }}
            tabIndex={0}
            role="menuitem"
            className="broker-unselected"
            data-testid={`my_broker_selector-option-broker_${broker.officeId}`}
            onClick={onBrokerChange}
            onKeyDown={handleKeyDown}
        >
            <StyledUnselectedBrokerImage alt="" src={UrlManager.brokerLogoSmall(broker.officeId)} />
            <ListItemText classes={{ root: "menuitem-text-root" }} primary={broker.brokerName} />
        </MenuItem>
    );
};

type UnselectedBrokerContainerProps = {
    broker: MyBrokerApi.Broker;
    onBrokerSelect: () => void;
};

const UnselectedBrokerContainer: FC<UnselectedBrokerContainerProps> = ({ broker, onBrokerSelect }) => {
    return (
        <>
            <LineSeparator />
            <UnselectedBroker broker={broker} onBrokerChange={onBrokerSelect} />
        </>
    );
};

type MyBrokerSelectorProps = {
    onSelect: (broker: MyBrokerApi.Broker, availableBrokers: MyBrokerApi.Broker[]) => void;
};

const MyBrokerSelector: FC<MyBrokerSelectorProps> = ({ onSelect }) => {
    const { t } = useTranslation();
    const { user, userState } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [hideSeeMore, setHideSeeMore] = useState(true);

    const {
        data: { result: availableBrokers },
    } = brokerApi.useQueryBrokers();

    const currentStateBroker = availableBrokers.find((item) => item.officeId === user.currentBrokerId);

    const onClickSelectedBroker = (e: MouseEvent | React.KeyboardEvent) => {
        setHideSeeMore(true);
        setAnchorEl(e.currentTarget);
        const elmntHeight = document.getElementById("welcomeText")?.offsetHeight;
        if (elmntHeight >= 60) {
            setHideSeeMore(false);
        }

        tagManager.pushBrokerInfoEvent();
    };

    const handleKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === "Enter") {
            onClickSelectedBroker(ev);
        }
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleBrokerSelect = async (broker: MyBrokerApi.Broker) => {
        handleCloseMenu();
        tagManager.pushUserPropertyOfficeId(broker.officeId);
        onSelect(broker, availableBrokers);
    };

    const getUnselectedAvailableBrokers = () => {
        return availableBrokers
            ?.filter((a) => a.officeId !== currentStateBroker?.officeId)
            .map((broker) => (
                <UnselectedBrokerContainer
                    key={broker.officeId}
                    broker={broker}
                    onBrokerSelect={() => handleBrokerSelect(broker)}
                />
            ));
    };

    const handleClickAddress = () => {
        window.open(`https://maps.google.com/?q=${currentStateBroker.address} ${currentStateBroker.zipCode}`, "_blank");
        setAnchorEl(null);
    };

    const handleClickUrl = () => {
        window.open(currentStateBroker.url, "_blank");
        setAnchorEl(null);
    };

    const handleClickPhone = () => {
        window.location.assign(`tel:${currentStateBroker.phone}`);
        setAnchorEl(null);
    };

    const handleClickEmail = () => {
        window.location.assign(`mailto:${currentStateBroker.email}`);
        setAnchorEl(null);
    };

    const showAddABrokerButton = !isABroker() && userState !== UserState.PENDING;

    return (
        <>
            <StyledBrokerButton
                aria-controls="customized-menu-broker-selector"
                data-testid="my_broker_selector-select-broker"
                $active={!!anchorEl}
                title={t("Common.Broker")}
                className="broker-button"
                onClick={onClickSelectedBroker}
                onKeyDown={handleKeyDown}
            >
                <div className="broker-button-image">
                    {currentStateBroker?.officeId && (
                        <img className="broker-image" alt="" src={UrlManager.brokerLogo(currentStateBroker.officeId)} />
                    )}
                </div>
                <div className="broker-button-container">
                    <div className="d-flex align-center">
                        <div className="broker-button-name mr10">{currentStateBroker?.brokerName}</div>
                        <RafterDownIcon
                            className="dropdown-arrow"
                            style={!anchorEl ? null : { transform: "rotate(180deg)" }}
                        />
                    </div>
                </div>
            </StyledBrokerButton>
            <Hidden smDown>
                <StyledAccountMenu
                    classes={{ paper: "menu-paper", list: "menu-list" }}
                    id="customized-menu-broker-selector"
                    keepMounted
                    elevation={0}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    data-testid="broker_selector_container"
                >
                    <CurrentBrokerInfo
                        hideSeeMore={hideSeeMore}
                        handleClickAddress={handleClickAddress}
                        handleClickUrl={handleClickUrl}
                        handleClickEmail={handleClickEmail}
                        handleClickPhone={handleClickPhone}
                        currentStateBroker={currentStateBroker}
                    />

                    {!isABroker() && getUnselectedAvailableBrokers()}
                    {showAddABrokerButton && <LineSeparator />}
                    {showAddABrokerButton && <AddBrokerButton />}
                </StyledAccountMenu>
            </Hidden>
            <Hidden smUp>
                <StyledModalMyBrokerSelectorMobile
                    hideBackdrop
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    title={t("MyBrokerSelector.MyBroker")}
                >
                    <MyBrokerSelectorMobile
                        hideSeeMore={hideSeeMore}
                        handleClickAddress={handleClickAddress}
                        handleClickUrl={handleClickUrl}
                        handleClickEmail={handleClickEmail}
                        handleClickPhone={handleClickPhone}
                        currentStateBroker={currentStateBroker}
                        UnselectedAvailableBrokers={getUnselectedAvailableBrokers()}
                    />
                </StyledModalMyBrokerSelectorMobile>
            </Hidden>
        </>
    );
};

export default MyBrokerSelector;
